import React, { useState } from "react";
import { Modal, Button, Spinner, Alert } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { cancelSubscription } from "../../redux/slices/subscriptionPaymentSlice";
import { refreshUserProfile } from "../../redux/slices/authSlice";
import { FiX } from "react-icons/fi";
import { showSuccess } from "../../components/toasts";
import Select from "react-select";
import { Form } from "react-bootstrap";
const CancelSubscriptionModal = ({ show, onClose }) => {
  const dispatch = useDispatch();

  const [isProcessing, setIsProcessing] = useState(false);
  const [reason, setReason] = useState(null);
  const [feedback, setFeedback] = useState("");
  const [error, setError] = useState(null);
  const store = useSelector((state) => state.auth);
  const cancellationOptions = [
    { value: "too_expensive", label: "Too Expensive" },
    { value: "not_using_enough", label: "Not Using Enough" },
    { value: "missing_features", label: "Missing Features" },
    { value: "found_alternative", label: "Found Alternative" },
    { value: "technical_issues", label: "Technical Issues" },
    { value: "temporary_break", label: "Taking a Temporary Break" },
    { value: "other", label: "Other" },
  ];
  const handleCancelSubscription = async () => {
    if (isProcessing) return;
    if (!reason) {
      setError("Please select a cancellation reason.");
      return;
    }
    if (reason?.value === "other" && !feedback.trim()) {
      setError("Please provide additional feedback.");
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // const payload = { userId: store?.user?.id };
      const payload = {
        userId: store?.user?.id,
        cancellation_reason: reason?.value || "",
        cancellation_feedback: feedback,
      };

      await dispatch(cancelSubscription(payload)).unwrap();

      // Refresh the redux user so subscription_status/cancellation_reason
      // propagate immediately — the trial banner switches to its "cancelled"
      // copy and the Cancel button hides on the next render.
      await dispatch(refreshUserProfile());

      showSuccess("Your subscription has been cancelled successfully.", {
        position: "top-right",
        autoClose: 5000,
      });

      onClose();
    } catch (err) {
      const errorMessage =
        typeof err === "string"
          ? err
          : err?.message || "Failed to cancel subscription. Please try again.";

      setError(errorMessage);
      console.error("Cancellation error:", err);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      backdrop="static"
      dialogClassName="custom-modal confirm-upgrade-summary"
    >
      <Modal.Header>
        <h5 className="modal-title">Cancel Subscription</h5>
        <Button className="close-btn" onClick={onClose}>
          <FiX size={24} />
        </Button>
      </Modal.Header>

      <Modal.Body>
        <div className="content">
          <div className="mb-2">
            <div className="plan-content">
              <span className="text-muted">Current Subscription Plan:</span>
              <strong>{store?.user?.plan?.name}</strong>
            </div>
            <div className="plan-content">
              <span className="text-muted">Plan Interval:</span>
              <strong>{store?.user?.plan?.interval}</strong>
            </div>
            <div className="plan-content">
              <span className="text-muted">Access until:</span>
              <strong>{store?.user?.plan_expiry_date || ""}</strong>
            </div>

            <div className="select-content">
              <Form.Group className="form-group">
                <Form.Label className="col-label">
                  Reason for cancellation
                </Form.Label>
                <Select
                  options={cancellationOptions}
                  placeholder="Select a reason"
                  value={reason}
                  onChange={(selectedOption) => {
                    setReason(selectedOption);

                    if (selectedOption?.value !== "other") {
                      setFeedback(""); // reset if not other
                    }
                  }}
                  classNamePrefix="react-select"
                  className="react-select-container"
                  isSearchable={false}
                  styles={{
                    menu: (base) => ({
                      ...base,
                      borderRadius: "15px",
                      marginTop: "8px",
                      backgroundColor: "var(--mmp-surface)",
                      padding: "6px",
                      zIndex: "1000",
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                    option: (base, state) => ({
                      ...base,
                      borderRadius: "8px",
                      padding: "10px",
                      backgroundColor: state.isFocused ? "var(--mmp-hover-bg)" : "var(--mmp-surface)",
                      color: "var(--mmp-text-primary)",
                      cursor: "pointer",
                      "&:active": {
                        backgroundColor: "var(--mmp-hover-bg)",
                      },
                    }),
                  }}
                />
              </Form.Group>
            </div>
            {reason?.value === "other" && (
              <div className="mt-3">
                <label className="form-label">Additional Feedback</label>
                <textarea
                  className="form-control"
                  rows={3}
                  placeholder="Write your feedback..."
                  value={feedback}
                  onChange={(e) => {
                    setFeedback(e.target.value);
                    if (e.target.value.trim()) {
                      setError(null); // remove error when user types
                    }
                  }}
                />
              </div>
            )}
            <hr className="my-3 mt-1" />
            <div className="mt-3">
              <p className="small text-muted mb-0">
                By clicking this button, your subscription will remain active
                until the end of your current billing period.
              </p>
            </div>
          </div>
          {error && (
            <Alert variant="danger" className="mt-3">
              {error}
            </Alert>
          )}
        </div>
        <div className="buttons text-center">
          <Button
            variant="secondary"
            // size="lg"
            onClick={handleCancelSubscription}
            disabled={isProcessing}
            className="subscribe-btn px-3 py-2"
          >
            {isProcessing ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  className="me-2"
                />
                Processing...
              </>
            ) : (
              "Cancel Subscription"
            )}
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default CancelSubscriptionModal;
